import React, { useState } from 'react';
import { useStrings } from '@/hooks/useStrings';
import { useApp } from '@/contexts/AppContext';
import { safeWrite } from '@/utils/supabaseWrite';
import PersonalizeStep from '@/components/onboarding/PersonalizeStep';
import IntentPicker from '@/components/onboarding/IntentPicker';
import PathAssessment from '@/components/onboarding/PathAssessment';
import BreathingDiscovery from '@/components/onboarding/BreathingDiscovery';
import FoundationLesson from '@/components/onboarding/FoundationLesson';
import ProgramReveal from '@/components/onboarding/ProgramReveal';

// PersonalizeStep sits at the very top so life stage + last period
// + top concerns are captured before the rest of the flow. Skippable
// in full or per-question; defaults are the most permissive
// (unspecified life stage, no period date, empty concerns) so the
// user is never blocked. IntentPicker still follows because it asks
// a different question (stated goal, e.g. "postpartum" vs concerns
// like "leakage"), and the path branching depends on intent.
const STEPS = [
  'PersonalizeStep',
  'IntentPicker',
  'PathAssessment',
  'FoundationLesson',
  'BreathingDiscovery',
  'ProgramReveal',
];

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    background: 'var(--cream, #FAF6F1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    overflow: 'auto',
  },
  container: {
    maxWidth: '420px',
    width: '100%',
    textAlign: 'center',
  },
  stepLabel: {
    fontFamily: "'DM Sans', 'Lato', system-ui, sans-serif",
    fontSize: '14px',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: 'var(--light-text, #6B5A4A)',
    marginBottom: '8px',
  },
  title: {
    fontFamily: "'Playfair Display', 'Cormorant Garamond', Georgia, serif",
    fontSize: '32px',
    fontWeight: 500,
    color: 'var(--text, #3D2B1F)',
    marginBottom: '16px',
    lineHeight: 1.3,
  },
  body: {
    fontFamily: "'DM Sans', 'Lato', system-ui, sans-serif",
    fontSize: '18px',
    lineHeight: 1.7,
    color: 'var(--light-text, #6B5A4A)',
    marginBottom: '40px',
  },
  cta: {
    width: '100%',
    height: '52px',
    border: 'none',
    borderRadius: 999,
    fontFamily: 'var(--font-ui)',
    fontWeight: 700,
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    cursor: 'pointer',
    background: 'var(--gold, #af004f)',
    color: 'var(--white, #ffffff)',
    transition: 'opacity 150ms ease',
  },
  progress: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    marginBottom: '32px',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    transition: 'background 200ms ease',
  },
};

function StepPlaceholder({ step, onNext }) {
  const t = useStrings();
  const titleKey = `onboarding_${step.toLowerCase()}_title`;
  const bodyKey = `onboarding_${step.toLowerCase()}_body`;
  const ctaKey = `onboarding_${step.toLowerCase()}_cta`;

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>{t(titleKey)}</h1>
      <p style={styles.body}>{t(bodyKey)}</p>
      <button type="button" style={styles.cta} onClick={onNext}>
        {t(ctaKey)}
      </button>
    </div>
  );
}

// Pick up where the user left off. Reads completion flags written by
// each sub-step into localStorage and lands on the first incomplete
// step instead of restarting from IntentPicker every visit. Keeps
// users from re-doing the assessment + foundation lesson if they
// bailed during the breath choreography on a flaky network.
function resumeStartStep() {
  let data = {};
  try {
    const saved = localStorage.getItem('bb7');
    if (saved) data = JSON.parse(saved);
  } catch {}
  // Step order: 0 PersonalizeStep, 1 IntentPicker, 2 PathAssessment,
  // 3 FoundationLesson, 4 BreathingDiscovery, 5 ProgramReveal.
  // PersonalizeStep is considered "done" if the user has any of
  // life_stage / cycle_last_period_start / concerns set OR if she has
  // already completed an intent (i.e., she is a returning user from
  // before the personalize step existed; do not loop her back to it).
  const personalizeDone = !!data.life_stage
    || !!data.cycle_last_period_start
    || (Array.isArray(data.concerns) && data.concerns.length > 0)
    || !!data.user_intent;
  if (!personalizeDone) return 0;
  if (!data.user_intent) return 1;
  if (!data.user_path) return 2;
  if (!data.foundation_lesson_complete) return 3;
  if (!data.breathing_discovery_complete) return 4;
  return 5;
}

export default function OnboardingFlow({ user, onComplete, onClose }) {
  const [currentStep, setCurrentStep] = useState(resumeStartStep);
  const [saving, setSaving] = useState(false);
  const { lang, patchProgress } = useApp();

  async function handleNext() {
    if (saving) return;

    // Last step: mark onboarding complete
    if (currentStep >= STEPS.length - 1) {
      setSaving(true);
      try {
        let data = {};
        try {
          const saved = localStorage.getItem('bb7');
          if (saved) data = JSON.parse(saved);
        } catch {}
        data.onboarding_complete = true;
        await safeWrite(user?.id, data);
        onComplete();
      } catch (err) {
        console.error('Onboarding save failed:', err);
        setSaving(false);
      }
      return;
    }

    setCurrentStep(currentStep + 1);
  }

  // IntentPicker callback. Stores the user's chosen reason on
  // user_progress.data.user_intent so the home greeting can
  // reference it back. Fire-and-forget — a slow Supabase write
  // must not block the onboarding handoff. patchProgress flips
  // the in-memory userProgress field on the same tick.
  //
  // Perimenopause shortcut: if the user picked "perimenopause" as
  // their reason for being here, mirror that into life_stage =
  // 'perimenopause' so /body opens to the menopause shelf on first
  // visit instead of the cycle arc + empty-state nudge. Uses the
  // canonical life_stage vocabulary (cycling / perimenopause /
  // menopause / postmenopause / unspecified); the normalizer bridges
  // it to menopause_stage. Users on other intents still get the unset
  // life_stage and discover MenopauseStageTile on Profile when ready.
  function handleIntent(intentKey) {
    const value = intentKey || 'unspecified';
    try {
      let data = {};
      try {
        const saved = localStorage.getItem('bb7');
        if (saved) data = JSON.parse(saved);
      } catch {}
      data.user_intent = value;
      const patch = { user_intent: value };
      if (value === 'perimenopause') {
        data.life_stage = 'perimenopause';
        patch.life_stage = 'perimenopause';
      }
      safeWrite(user?.id, data).catch(() => {});
      patchProgress(patch);
    } catch {}
    handleNext();
  }

  // Each sub-step calls onClose when Back is pressed on its own
  // first sub-screen. Route that back through the flow: if we're past
  // the first flow step, step backwards; otherwise dismiss the
  // overlay via the parent's onClose (Back on step 1, screen 1 → /home).
  function handleStepBack() {
    if (currentStep > 0) {
      setCurrentStep((n) => n - 1);
    } else if (onClose) {
      onClose();
    }
  }

  // Exit-to-home affordance threaded through every sub-step. Distinct
  // from the per-step Back (which decrements within the flow) and Skip
  // (which advances past one step) — onExit dismisses the entire
  // overlay so the user lands back on /home and can re-enter via the
  // "Start Here" CTA on WeekHome later. We hand each sub-step the
  // parent's onClose directly; sub-steps that don't want the chrome
  // (e.g. FoundationLesson in revisit mode) gate it themselves.
  const onExit = onClose;

  if (currentStep === 0) {
    return <PersonalizeStep onComplete={handleNext} />;
  }
  if (currentStep === 1) {
    return (
      <IntentPicker
        lang={lang}
        onNext={handleIntent}
        onSkip={() => handleIntent('unspecified')}
        onExit={onExit}
      />
    );
  }
  if (currentStep === 2) {
    return <PathAssessment onComplete={handleNext} onClose={handleStepBack} onExit={onExit} />;
  }
  if (currentStep === 3) {
    return <FoundationLesson onComplete={handleNext} onClose={handleStepBack} onExit={onExit} />;
  }
  if (currentStep === 4) {
    return <BreathingDiscovery onComplete={handleNext} onClose={handleStepBack} onExit={onExit} />;
  }
  if (currentStep === 5) {
    return <ProgramReveal onComplete={handleNext} onClose={handleStepBack} onExit={onExit} />;
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.progress}>
        {STEPS.map((_, i) => (
          <div
            key={i}
            style={{
              ...styles.dot,
              background: i <= currentStep
                ? 'var(--gold, #af004f)'
                : 'var(--beige, #E8DDD0)',
            }}
          />
        ))}
      </div>
      <StepPlaceholder step={STEPS[currentStep]} onNext={handleNext} />
    </div>
  );
}
